import { ref, computed } from "vue"
import { useI18n } from "vue-i18n"
import type { Address } from "@types"

// =====================
// Types
// =====================

/**
 * Type d'organisation
 */
export type CompanyType = "entreprise" | "asso" | "copro" | "collectivite"

/**
 * Formulaire d'entreprise
 */
export type CompanyFormData = {
    /**
     * Identifiant MongoDB (si entreprise existante)
     */
    _id?: string
    /**
     * Nom de l'organisation
     */
    name: string
    /**
     * Adresse de l'organisation
     */
    address: Address | null
    /**
     * Identifiant de l'organisation (SIREN, RNA, n° copro)
     */
    idcompany: string
    /**
     * Forme juridique (pour les entreprises)
     */
    legalForm: string
    /**
     * Type d'organisation
     */
    type: CompanyType | ""
}

/**
 * Options pour le composable
 */
export type UseCompanyFormOptions = {
    /**
     * Type d'organisation par défaut
     */
    defaultType?: CompanyType | ""
}

// =====================
// Constantes
// =====================

/**
 * Liste des formes juridiques disponibles
 */
export const LEGAL_FORM_OPTIONS = [
    "EARL",
    "EI",
    "EPIC",
    "EURL",
    "GAEC",
    "SA",
    "SARL",
    "SAS",
    "SASU",
    "SC",
    "SCA",
    "SCEA",
    "SCOP",
    "SCS",
    "SNC",
    "SCI",
    "ASSOCIATION",
    "COMMUNE",
    "ÉTABLISSEMENT PUBLIC ADMINISTRATIF",
] as const

// =====================
// Composable
// =====================

/**
 * Composable pour gérer le formulaire d'entreprise avec validation
 * @param options - Options de configuration
 * @returns Formulaire, validation et options
 */
export function useCompanyForm(options: UseCompanyFormOptions = {}) {
    const { t } = useI18n({
        useScope: "global",
        messages: {
            fr: {
                companyForm: {
                    options: {
                        entreprise: "Entreprise",
                        asso: "Association",
                        copro: "Copropriété",
                        collectivite: "Collectivité",
                    },
                    errors: {
                        id_company:
                            "Le numéro SIREN doit contenir exactement 9 chiffres",
                        id_association:
                            "Le numéro RNA doit contenir 9 chiffres ou commencer par W suivi de 9 chiffres",
                        id_copro:
                            "Le numéro d'immatriculation doit contenir 2 lettres suivies de 7 chiffres",
                    },
                },
            },
        },
    })

    // =====================
    // État du formulaire
    // =====================

    const form = ref<CompanyFormData>({
        name: "",
        address: null,
        idcompany: "",
        legalForm: "",
        type: options.defaultType || "",
    })

    // =====================
    // Options
    // =====================

    /**
     * Options de types d'organisation
     */
    const companyTypeOptions = computed(() => [
        {
            value: "entreprise",
            label: t("companyForm.options.entreprise"),
        },
        {
            value: "asso",
            label: t("companyForm.options.asso"),
        },
        {
            value: "copro",
            label: t("companyForm.options.copro"),
        },
        {
            value: "collectivite",
            label: t("companyForm.options.collectivite"),
        },
    ])

    /**
     * Options de formes juridiques
     */
    const legalFormOptions = computed(() =>
        LEGAL_FORM_OPTIONS.map((lf) => ({
            value: lf,
            label: lf,
        }))
    )

    // =====================
    // Validation
    // =====================

    /**
     * Vérifie si l'identifiant de l'entreprise est valide selon le type
     */
    function isIdCompanyValid(type: CompanyType | "", value: string): boolean {
        if (!value) return false

        const patterns: Record<string, RegExp[]> = {
            asso: [/^\d{9}$/, /^W\d{9}$/],
            copro: [/^[a-zA-Z]{2}\d{7}$/],
            entreprise: [/^\d{9}$/],
        }

        if (!patterns[type]) return true // collectivite n'a pas besoin d'identifiant

        // Pour copro, on autorise une valeur vide
        if (type === "copro" && value.length === 0) return true

        return patterns[type].some((regex) => regex.test(value))
    }

    /**
     * Message d'erreur pour l'identifiant de l'entreprise
     */
    const companyIdErrorMessage = computed<string | null>(() => {
        if (!form.value.type || !form.value.idcompany) return null

        switch (form.value.type) {
            case "asso":
                if (!isIdCompanyValid("asso", form.value.idcompany)) {
                    return t("companyForm.errors.id_association")
                }
                break
            case "copro":
                if (!isIdCompanyValid("copro", form.value.idcompany)) {
                    return t("companyForm.errors.id_copro")
                }
                break
            case "entreprise":
                if (!isIdCompanyValid("entreprise", form.value.idcompany)) {
                    return t("companyForm.errors.id_company")
                }
                break
        }
        return null
    })

    /**
     * Vérifie si le formulaire est valide
     */
    const isValidCompany = computed<boolean>(() => {
        // Champs obligatoires de base
        const hasBasicFields =
            !!form.value.name && !!form.value.address && !!form.value.type

        if (!hasBasicFields) return false

        // Validation spécifique selon le type
        if (form.value.type === "entreprise") {
            // Entreprise: SIREN + forme juridique obligatoires
            return (
                !!form.value.legalForm &&
                !!form.value.idcompany &&
                !companyIdErrorMessage.value
            )
        }

        if (
            form.value.type === "asso" ||
            form.value.type === "copro" ||
            form.value.type === "entreprise"
        ) {
            // Asso/Copro: identifiant obligatoire et valide
            return !!form.value.idcompany && !companyIdErrorMessage.value
        }

        // Collectivité: pas d'identifiant requis
        return true
    })

    // =====================
    // Actions
    // =====================

    /**
     * Réinitialise le formulaire
     */
    function resetForm() {
        form.value = {
            name: "",
            address: null,
            idcompany: "",
            legalForm: "",
            type: options.defaultType || "",
        }
    }

    /**
     * Met à jour le formulaire avec des données existantes
     */
    function setFormData(data: Partial<CompanyFormData>) {
        form.value = {
            ...form.value,
            ...data,
        }
    }

    /**
     * Gère le changement de type d'organisation
     */
    function onTypeChange() {
        // Si on n'est plus une entreprise, réinitialiser la forme juridique
        if (form.value.type !== "entreprise") {
            form.value.legalForm = ""
        }
        // Si on est une collectivité, réinitialiser l'identifiant
        if (form.value.type === "collectivite") {
            form.value.idcompany = ""
        }
    }

    return {
        // État
        form,

        // Options
        companyTypeOptions,
        legalFormOptions,

        // Validation
        isValidCompany,
        companyIdErrorMessage,
        isIdCompanyValid,

        // Actions
        resetForm,
        setFormData,
        onTypeChange,
    }
}
